import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/providers/AuthProvider';
import { simulationService } from '@/api/simulationService';
import {
    getSessionQuestions,
    getVirtualSimulationState,
    startSession,
    submitSession
} from '@/api/studentVirtualSimulationService';
import { parseApiError } from '@/utils/errorHandler';
import type { parsedApiError } from '@/types/api/errors.api';
import type { question, simulation } from '@/features/simulationConfiguration/types.api';
import { useAnswerManager } from './useAnswerManager';
import { useQuestionManager } from './useQuestionManager';

type simulationState = {
    simulation: simulation | null;
    status: 'loading' | 'not_started' | 'in_progress_s1' | 'break' | 'in_progress_s2' | 'finished';
    sessionStartTime: string | null;
    isLoading: boolean;
    isSubmitting: boolean;
    error: parsedApiError | null;
};

export const useVirtualSimulation = () => {
    const { simulationId } = useParams<{ simulationId: string }>();
    const navigate = useNavigate();
    const { user } = useAuth();

    const [state, setState] = useState<simulationState>({
        simulation: null,
        status: 'loading',
        sessionStartTime: null,
        isLoading: true,
        isSubmitting: false,
        error: null,
    });
    const [questions, setQuestions] = useState<question[]>([]);
    const [initialAnswers, setInitialAnswers] = useState<Record<string, string>>({});

    const { answers, handleAnswerChange } = useAnswerManager(
        simulationId,
        initialAnswers,
        state.status
    );
    const {
        currentQuestion,
        currentQuestionIndex,
        totalQuestions,
        goToQuestion,
        goToNext,
        goToPrevious
    } = useQuestionManager(questions);

    const currentSessionNumber = useMemo(() => {
        if (state.status === 'not_started' || state.status === 'in_progress_s1') return 1;
        if (state.status === 'break' || state.status === 'in_progress_s2') return 2;
        return null;
    }, [state.status]);

    const loadAndPrepareQuestions = useCallback(async (sessionNum: number, simId: string, simData: simulation) => {
        let allQuestions: question[] = [];
        const sessionObj = simData.sessions?.find(s => s.number === sessionNum);
        if (sessionObj?.tests) {
            for (const test of sessionObj.tests) {
                const questionsData = await getSessionQuestions(simId, sessionNum, test.id);
                allQuestions = allQuestions.concat(questionsData as unknown as question[]);
            }
        }
        return allQuestions.map((q, index) => ({ ...q, display_number: index + 1 }));
    }, []);

    useEffect(() => {
        if (!simulationId || !user) return;
        const loadInitialData = async () => {
            try {
                setState(s => ({ ...s, isLoading: true, error: null }));
                const [simData, simState] = await Promise.all([
                    simulationService.getById(simulationId),
                    getVirtualSimulationState(simulationId),
                ]);

                let loadedQuestions: question[] = [];
                if (simState.status === 'in_progress_s1' || simState.status === 'in_progress_s2') {
                    const sessionNum = simState.status === 'in_progress_s1' ? 1 : 2;
                    loadedQuestions = await loadAndPrepareQuestions(sessionNum, simulationId, simData);
                }

                setQuestions(loadedQuestions);
                setInitialAnswers(simState.current_answers || {});
                setState(s => ({
                    ...s,
                    simulation: simData,
                    status: simState.status as simulationState['status'],
                    sessionStartTime: simState.session_1_start_time || simState.session_2_start_time,
                    isLoading: false,
                }));
            } catch (err) {
                setState(s => ({ ...s, isLoading: false, error: parseApiError(err) }));
            }
        };
        loadInitialData();
    }, [simulationId, user, loadAndPrepareQuestions]);

    const handleStartSession = useCallback(async () => {
        if (!simulationId || !currentSessionNumber || !state.simulation) return;
        try {
            setState(s => ({ ...s, isSubmitting: true }));
            const response = await startSession(simulationId, currentSessionNumber);
            const loadedQuestions = await loadAndPrepareQuestions(currentSessionNumber, simulationId, state.simulation);

            setQuestions(loadedQuestions);
            setState(s => ({
                ...s,
                status: response.status as simulationState['status'],
                sessionStartTime: response.start_time,
                isSubmitting: false,
            }));
        } catch (err) {
            setState(s => ({ ...s, isSubmitting: false, error: parseApiError(err) }));
        }
    }, [simulationId, currentSessionNumber, state.simulation, loadAndPrepareQuestions]);

    const handleSubmitSession = useCallback(async (options: { force?: boolean } = {}) => {
        if (!simulationId || !currentSessionNumber) return;
        if (!options.force && !window.confirm("¿Estás seguro de que quieres finalizar? No podrás cambiar tus respuestas.")) return;

        try {
            setState(s => ({ ...s, isSubmitting: true }));
            await submitSession(simulationId, currentSessionNumber, answers);

            const nextStatus = currentSessionNumber === 1 ? 'break' : 'finished';

            setQuestions([]);
            setInitialAnswers({});
            setState(s => ({ ...s, status: nextStatus, isSubmitting: false, sessionStartTime: null }));

            if (nextStatus === 'finished') {
                navigate('/student-dashboard');
            }
        } catch (err) {
            setState(s => ({ ...s, isSubmitting: false, error: parseApiError(err) }));
        }
    }, [simulationId, currentSessionNumber, answers, navigate]);

    return {
        state,
        currentSessionNumber,

        answers,
        questions,
        handleAnswerChange,

        currentQuestion,
        currentQuestionIndex,
        totalQuestions,
        goToQuestion,
        goToNext,
        goToPrevious,

        handleStartSession,
        handleSubmitSession,
    };
};
